import { isMarketHours as _isMarketHours, IndexSymbol } from '@options-oracle/types'
import { CONFIG } from '../config/constants'
import type { MarketSnapshot, OptionData } from '../types'
import { calculateSupportResistance, calculateVarianceMetrics } from './analytics'
import { UpstoxOptionChainSchema, CSVRowSchema } from '@options-oracle/types'

/**
 * Format Date to DD-MM-YYYY
 */
export function formatDateToDDMMYYYY(date: Date): string {
    const d = String(date.getDate()).padStart(2, '0')
    const m = String(date.getMonth() + 1).padStart(2, '0') // Month is 0-indexed
    const y = date.getFullYear()
    return `${d}-${m}-${y}`
}

/**
 * Convert Timestamp to IST ISO String
 */
export const toIST = (timestamp: number | string | undefined): string => {
    if (!timestamp) return ''
    try {
        // Check if timestamp is epoch (number or string number)
        if (!Number.isNaN(Number(timestamp))) {
            const date = new Date(Number(timestamp))
            // Add 5 hours 30 minutes
            const offset = 5.5 * 60 * 60 * 1000
            const istDate = new Date(date.getTime() + offset)
            return istDate.toISOString().replace('Z', '+05:30')
        }
        // If already string
        return String(timestamp)
    } catch (e) {
        return String(timestamp)
    }
}

/**
 * Parse Scientific Notation or String Numbers
 */
export const parseScientificNotation = (val: string | number | undefined): number => {
    if (val === undefined || val === null || val === '') return 0
    if (typeof val === 'number') return val
    if (typeof val === 'string') {
        const clean = val.replace(/["',]/g, '')

        // Check for scientific notation
        if (clean.toLowerCase().includes('e')) {
            return Number(clean)
        }
        return parseFloat(clean) || 0
    }
    return 0
}

/**
 * Detect Date Format from a list of strings
 */
function detectDateFormat(dates: string[]): 'DMY' | 'MDY' {
    const SAMPLE_SIZE = 50 // Check 50 random samples

    // Random Sampling for better distribution
    const samples =
        dates.length <= SAMPLE_SIZE
            ? dates
            : Array.from({ length: SAMPLE_SIZE }, () => dates[Math.floor(Math.random() * dates.length)])

    let dmyCount = 0
    let mdyCount = 0

    for (const d of samples) {
        if (!d) continue
        const clean = d.trim().replace(/['"]/g, '')
        // Only check ambiguous numeric formats (e.g. 02/03/2026), ignore DD-MMM-YYYY
        if (/[a-zA-Z]/.test(clean)) continue

        const parts = clean.split(/[-/.]/)
        if (parts.length >= 3) {
            const p1 = parseInt(parts[0]!, 10)
            const p2 = parseInt(parts[1]!, 10)

            // Check validity
            if (!isNaN(p1) && !isNaN(p2)) {
                if (p1 > 12) dmyCount++ // First part > 12 -> Must be Day -> DMY
                if (p2 > 12) mdyCount++ // Second part > 12 -> Must be Day -> MDY (assuming year is last)
            }
        }
    }

    // Strong preference for DMY in Indian Context (NSE/BSE)
    // Only switch to MDY if we have strong evidence (numbers > 12 in 2nd position)
    // AND NO evidence for DMY (numbers > 12 in 1st position).
    if (mdyCount > 0 && dmyCount === 0) return 'MDY'

    return 'DMY' // Default to DMY (Indian/UK Standard)
}

/**
 * Robust Date Parser
 * Handles: DD-MM-YYYY, DD/MM/YYYY, YYYY-MM-DD, DD-MMM-YYYY
 * NEW: Supports format hint 'DMY' or 'MDY'
 */
export function parseDateStr(dateStr: string | any, format: 'DMY' | 'MDY' = 'DMY'): Date | null {
    if (!dateStr) return null
    const cleanStr = String(dateStr).trim().replace(/['"]/g, '')
    // ... (rest of logic)

    try {
        // 1. Try ISO/Standard Date Parse first (fastest)
        // Check if it's already a valid full date string (e.g. "2024-12-26T09:16:00")
        // STRICTLY restrict to YYYY- starting strings to avoid Native JS parsing "03-02-2026" as MDY (US Default)
        // ignoring our detected format.
        const isISO = /^\d{4}/.test(cleanStr)
        if (isISO) {
            const d = new Date(cleanStr)
            if (!isNaN(d.getTime()) && cleanStr.length > 8) {
                // Validate year sanity to avoid 2049/future issues
                const y = d.getFullYear()
                if (y > 2000 && y < 2040) return d
            }
        }

        // 2. Custom Regex Parsing (Robust)
        // Matches: DD-MMM-YYYY, DD/MM/YYYY, DD-MM-YY, YYYY-MM-DD
        // Also optionally matches Time: HH:mm[:ss]

        // Split Date and Time
        const parts = cleanStr.split(/\s+|T/)
        const datePart = parts[0] || ''
        const timePart = parts.length > 1 ? parts[1] || '' : ''

        const dateSegments = datePart.split(/[-/.]/)

        let day = 0,
            month = 0,
            year = 0

        if (dateSegments.length === 3) {
            const s1 = dateSegments[0] || ''
            const s2 = dateSegments[1] || ''
            const s3 = dateSegments[2] || ''

            const months: { [key: string]: number } = {
                jan: 0,
                feb: 1,
                mar: 2,
                apr: 3,
                may: 4,
                jun: 5,
                jul: 6,
                aug: 7,
                sep: 8,
                oct: 9,
                nov: 10,
                dec: 11,
            }

            // Check for Month Name in 2nd position (DD-MMM-YYYY)
            // Unambiguous, format hint ignored
            const monStr = s2.toLowerCase().substring(0, 3)
            if (months[monStr] !== undefined) {
                day = parseInt(s1, 10)
                month = months[monStr]
                year = parseInt(s3, 10)
            }
            // Check YYYY-MM-DD (ISO-like)
            // Unambiguous
            else if (s1.length === 4) {
                year = parseInt(s1, 10)
                month = parseInt(s2, 10) - 1
                day = parseInt(s3, 10)
            }
            // Assume DD-MM-YYYY or DD-MM-YY or MM-DD-YYYY based on format
            else {
                if (format === 'MDY') {
                    month = parseInt(s1, 10) - 1
                    day = parseInt(s2, 10)
                    year = parseInt(s3, 10)
                } else {
                    day = parseInt(s1, 10)
                    month = parseInt(s2, 10) - 1
                    year = parseInt(s3, 10)
                }
            }

            // Handle 2-digit Year (e.g., "24" -> 2024)
            if (year < 100) year += 2000

            // Basic Validation
            if (month < 0 || month > 11 || day < 1 || day > 31) return null

            // Construct Date (Local Time)
            const resultDate = new Date(year, month, day)

            // Add Time if present
            if (timePart) {
                const timeSegments = timePart.split(':')
                if (timeSegments.length >= 2) {
                    const h = parseInt(timeSegments[0] || '0', 10)
                    const m = parseInt(timeSegments[1] || '0', 10)
                    const s = timeSegments[2] ? parseInt(timeSegments[2] || '0', 10) : 0
                    resultDate.setHours(h, m, s)
                }
            }

            return resultDate
        }

        return null
    } catch (e) {
        return null // Fail gracefully
    }
}

// ... (Rest of file unchanged until getCanonicalSymbol) ... Note: getCanonicalSymbol is at line 164.
// I need to only replace up to line 159. But wait, I need to update rowsToSnapshots too which is at line 326.
// I'll execute separate ReplaceFileContent for rowsToSnapshots to handle file size chunks.

/**
 * Get Canonical Symbol Name
 */
export function getCanonicalSymbol(raw: string): string {
    if (!raw) return 'UNKNOWN'
    const upper = raw.toUpperCase().trim()

    if (upper.includes('SENSEX')) return CONFIG.SYMBOLS[1] // SENSEX
    if (upper.includes('BANKEX')) return 'BANKEX'
    if (upper.includes('NIFTY')) return CONFIG.SYMBOLS[0] // NIFTY (Default if checked last)

    return upper
}

export const isMarketHours = () => _isMarketHours()

/**
 * Get Current IST Time String
 */
export const getCurrentIST = (): string => {
    return new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })
}

/**
 * Format Open Interest (e.g. 1.5M, 20K)
 */
export const formatOI = (oi: number | undefined): string => {
    if (oi === undefined || oi === null) return '-'
    const absOi = Math.abs(oi)
    const sign = oi < 0 ? '-' : ''
    
    if (absOi >= 10000000) return sign + (absOi / 10000000).toFixed(2) + ' Cr'
    if (absOi >= 100000) return sign + (absOi / 100000).toFixed(2) + ' L'
    if (absOi >= 1000) return sign + (absOi / 1000).toFixed(2) + ' K'
    return oi.toString()
}

/**
 * Format Volume
 */
export const formatVolume = (vol: number | undefined): string => {
    if (vol === undefined || vol === null) return '-'
    return formatOI(vol)
}

/**
 * Format Combined OI (always in Lakhs)
 * User requirement: Combined OI shown in Lakhs across the app
 */
export const formatCombinedOI = (value: number | undefined): string => {
    if (value === undefined || value === null || value === 0) return '-'
    const absValue = Math.abs(value)
    const sign = value < 0 ? '-' : ''
    const lakhs = absValue / 100000
    if (lakhs >= 100) return sign + lakhs.toFixed(0) + ' L'
    if (lakhs >= 10) return sign + lakhs.toFixed(1) + ' L'
    return sign + lakhs.toFixed(2) + ' L'
}

/**
 * Format Combined Volume (always in Crores)
 * User requirement: Combined Volume shown in Crores across the app
 */
export const formatCombinedVol = (value: number | undefined): string => {
    if (value === undefined || value === null || value === 0) return '-'
    const absValue = Math.abs(value)
    const sign = value < 0 ? '-' : ''
    const crores = absValue / 10000000
    if (crores >= 100) return sign + crores.toFixed(0) + ' Cr'
    if (crores >= 10) return sign + crores.toFixed(1) + ' Cr'
    if (crores >= 1) return sign + crores.toFixed(2) + ' Cr'
    // If less than 1 Cr, show in Lakhs
    const lakhs = absValue / 100000
    if (lakhs >= 10) return sign + lakhs.toFixed(1) + ' L'
    return sign + lakhs.toFixed(2) + ' L'
}

/**
 * Format number to locale string with custom handling
 */
export const formatNumber = (
    value: number | undefined,
    style: 'currency' | 'decimal' | 'percent' = 'decimal',
): string => {
    if (value === undefined || value === null || isNaN(value)) return '-'

    if (style === 'currency') {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 2,
        }).format(value)
    }

    if (style === 'percent') {
        return new Intl.NumberFormat('en-IN', {
            style: 'percent',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value / 100) // Expects 0.1 for 10%
    }

    // Decimal
    return new Intl.NumberFormat('en-IN', {
        maximumFractionDigits: 2,
    }).format(value)
}

/**
 * Custom Simple CSV Parser
 * Handles standard CSV with headers and quoted values.
 */
export const parseCSV = (text: string): any[] => {
    // Optimized string split: RegEx split(/\r?\n/) is slow on large 15MB+ CSV strings.
    const lines = text.split('\n')
    if (lines.length === 0) return []

    // Remove BOM if present
    const cleanHeaderLine = (lines[0] || '').replace(/^\uFEFF/, '')
    // Parse headers handling quotes
    const parseLine = (line: string) => {
        const result = []
        let start = 0
        let inQuotes = false
        for (let i = 0; i < line.length; i++) {
            if (line[i] === '"') {
                inQuotes = !inQuotes
            } else if (line[i] === ',' && !inQuotes) {
                let val = line.substring(start, i).trim()
                // Remove surrounding quotes if present
                if (val.startsWith('"') && val.endsWith('"')) {
                    val = val.substring(1, val.length - 1)
                }
                result.push(val)
                start = i + 1
            }
        }
        let lastVal = line.substring(start).trim()
        if (lastVal.startsWith('"') && lastVal.endsWith('"')) {
            lastVal = lastVal.substring(1, lastVal.length - 1)
        }
        result.push(lastVal)
        return result
    }

    const headers = parseLine(cleanHeaderLine).map((h) => h.replace(/['"]/g, '').trim())
    const rows = []

    for (let i = 1; i < lines.length; i++) {
        let currentLine = lines[i] || ''
        if (currentLine.charCodeAt(currentLine.length - 1) === 13) currentLine = currentLine.slice(0, -1)
        if (currentLine.trim() === '') continue

        const values = parseLine(currentLine)

        // Best effort mapping
        const row: any = {}
        headers.forEach((header, index) => {
            let val: string | number | undefined = values[index]
            if (val !== undefined) {
                // Try Number
                val = val.replace(/,/g, '') // Remove commas from numbers inside quotes if any remain
                if (!isNaN(Number(val)) && val !== '') {
                    val = Number(val)
                }
            }
            row[header] = val
        })
        
        // Zod boundary for CSV rows
        try {
            const validatedRow = CSVRowSchema.parse(row);
            rows.push(validatedRow);
        } catch (e) {
            // Drop malformed row silently to prevent crashes
        }
    }
    return rows
}

/**
 * Convert Flat Rows (from CSV/API) to MarketSnapshot Structure
 */
// ...
export const rowsToSnapshots = (rows: any[]): MarketSnapshot[] => {
    const snapshotsMap = new Map<string, MarketSnapshot>()

    // 1. Detect Date Format from Sample Rows
    const sampleDates: string[] = []
    for (let i = 0; i < Math.min(rows.length, 100); i++) {
        const r = rows[i]
        // Prioritize Expiry for detection as that's where the ambiguity hurts most
        const exp = r.expirationDate || r.expiry || r.Expiry || r.EXPIRY || r.expiration
        if (exp && typeof exp === 'string') sampleDates.push(exp)

        const ts = r.timestampIST || r.timestamp || r.Time || r.time || r.Date || r.date || r.datetime
        if (ts && typeof ts === 'string') sampleDates.push(ts)
    }

    const detectedFormat = detectDateFormat(sampleDates)

    // Helper: Normalize Time to Minute Resolution
    const getTimeKey = (ts: string | number | undefined | null) => {
        if (ts === undefined || ts === null) return 'unknown'

        // Handle Numeric Timestamp (Epoch)
        if (typeof ts === 'number') {
            // If looks like full MS timestamp -> Normalize to minute
            if (ts > 1000000000000) return new Date(ts).toISOString().substring(0, 16)
            return String(ts)
        }

        const tsStr = String(ts).trim()
        if (tsStr === '') return 'unknown'

        // Try to parse to date object to normalize
        try {
            // Basic cleaner
            const cleanTs = tsStr.replace(/^"|"$|'/g, '').trim()

            // Strict Minute Normalization using detected format
            const d = parseDateStr(cleanTs, detectedFormat)
            if (d) {
                // Actually, simply returning the HH:mm part of the ISO string (if it was parsed correctly)
                // might be safest if we trust parseDateStr handling.
                const offset = 5.5 * 60 * 60 * 1000
                const localDate = new Date(d.getTime() + offset)
                return localDate.toISOString().substring(0, 16)
            }

            return cleanTs.substring(0, 16)
        } catch (_e) {
            return String(ts).substring(0, 16)
        }
    }

    for (const row of rows) {
        // DEBUG: Log first row keys to identify header issues
        if (rows.indexOf(row) === 0) {
            // console.log( '[rowsToSnapshots] First Row Raw Keys:', Object.keys( row ) )
            // console.log( '[rowsToSnapshots] Detected Date Format:', detectedFormat )
        }

        // 1. Timestamp Detection
        let ts = row.timestampIST || row.timestamp || row.Time || row.time || row.Date || row.date || row.datetime

        if (ts && typeof ts === 'string') ts = ts.replace(/['"]/g, '').trim()

        if (!ts) {
            // Try numeric timestamp if present in other fields?
            // Unlikely for CSV.
            continue
        }

        // If numeric-like string or number
        if (!Number.isNaN(Number(ts)) && Number(ts) > 946684800000) {
            try {
                ts = toIST(Number(ts))
            } catch (_e) { console.warn('Ignored error in data-utils.ts:', _e); }
        }

        // NORMALIZATION: Handle DD-MMM-YYYY or DD-MM-YYYY formats
        if (!ts) continue

        const timeKey = getTimeKey(ts)

        if (!snapshotsMap.has(timeKey)) {
            // Convert timeKey/ts to accurate Unix Timestamp
            let unixTimestamp = 0
            const numTs = Number(ts)

            if (!Number.isNaN(numTs) && numTs > 946684800000) {
                unixTimestamp = numTs
            } else {
                // Pass detectedFormat
                const parsedDate = parseDateStr(ts, detectedFormat)
                if (parsedDate) {
                    const y = parsedDate.getFullYear()
                    const mo = String(parsedDate.getMonth() + 1).padStart(2, '0')
                    const da = String(parsedDate.getDate()).padStart(2, '0')
                    const ho = String(parsedDate.getHours()).padStart(2, '0')
                    const mi = String(parsedDate.getMinutes()).padStart(2, '0')
                    const se = String(parsedDate.getSeconds()).padStart(2, '0')
                    // parseDateStr returns local date components.
                    const isoIST = `${y}-${mo}-${da}T${ho}:${mi}:${se}+05:30`
                    unixTimestamp = new Date(isoIST).getTime()
                } else {
                    try {
                        const d = new Date(ts)
                        if (!isNaN(d.getTime())) unixTimestamp = d.getTime()
                        else unixTimestamp = Date.now()
                    } catch (_e) {
                        unixTimestamp = Date.now()
                    }
                }
            }
            if (Number.isNaN(unixTimestamp) || unixTimestamp === 0) unixTimestamp = Date.now()

            let normalizedIST = ts
            try {
                const d = new Date(unixTimestamp)
                const offsetMs = 5.5 * 60 * 60 * 1000
                const istTime = new Date(d.getTime() + offsetMs)
                normalizedIST = istTime.toISOString().replace('Z', '+05:30')
            } catch (_e) { console.warn('Ignored error in data-utils.ts:', _e); }

            snapshotsMap.set(timeKey, {
                timestamp: unixTimestamp,
                timestampIST: normalizedIST,
                originalTimestampStr: ts,
                indexData: {},
                optionsData: {},
                vixData: { value: 0, change: 0, changePercent: 0 },
            })
        }

        const snap = snapshotsMap.get(timeKey)!

        // VIX Data Merge
        if (row.VIX_value && (!snap.vixData?.value || snap.vixData.value === 0)) {
            snap.vixData = {
                value: row.VIX_value || 0,
                change: row.VIX_change || 0,
                changePercent: row.VIX_changePercent || 0,
            }
        }

        // 2. Symbol Detection
        let symbol =
            row.underlyingName || row.symbol || row.Symbol || row.Underlying || row.ticker || row.instrument_name

        if (symbol && typeof symbol === 'string') {
            symbol = getCanonicalSymbol(symbol)
        } else {
            // If no symbol, try to infer from index data headers presence
            if (row.Index_lastPrice || row.spotPrice) {
                const p = row.Index_lastPrice || row.spotPrice
                if (p > 70000)
                    symbol = CONFIG.SYMBOLS[1] // SENSEX (approx > 70k) - Overlaps but usually higher
                else if (p > 1000) symbol = CONFIG.SYMBOLS[0] // NIFTY

                // Refined logic:
                // SENSEX usually > 70k
                // NIFTY ~ 15k-30k
                if (p > 70000) symbol = CONFIG.SYMBOLS[1]
                else symbol = CONFIG.SYMBOLS[0]
            }
        }

        // 3. Expiry Detection
        let expiry = row.expirationDate || row.expiry || row.Expiry || row.EXPIRY || row.expiration
        if (expiry && typeof expiry === 'string') expiry = expiry.replace(/['"]/g, '').trim()

        // Normalize expiry to DD-MM-YYYY using detected format
        if (expiry) {
            try {
                const d = parseDateStr(expiry, detectedFormat)
                if (d) expiry = formatDateToDDMMYYYY(d)
            } catch (_e) { console.warn('Ignored error in data-utils.ts:', _e); }
        }

        // Index Data (Merge/Update)
        if (symbol) {
            // Always set/update index data for the symbol if not present
            // Handle aliases for Spot Price
            const idxLtp =
                row.Index_lastPrice ||
                row.SpotPrice ||
                row.spotPrice ||
                row.underlyingValue ||
                row['Index Value'] ||
                row['Underlying Index'] ||
                row['Index Price'] ||
                0

            if (!snap.indexData) snap.indexData = {}
            const indexData = snap.indexData

            if (!indexData[symbol] || (idxLtp && idxLtp !== 0)) {
                indexData[symbol] = {
                    symbol: symbol,
                    open:
                        typeof row.Index_open === 'string'
                            ? parseScientificNotation(row.Index_open)
                            : row.Index_open || 0,
                    high:
                        typeof row.Index_high === 'string'
                            ? parseScientificNotation(row.Index_high)
                            : row.Index_high || 0,
                    low:
                        typeof row.Index_low === 'string' ? parseScientificNotation(row.Index_low) : row.Index_low || 0,
                    close:
                        typeof row.Index_close === 'string'
                            ? parseScientificNotation(row.Index_close)
                            : row.Index_close || 0,
                    lastPrice: typeof idxLtp === 'string' ? parseScientificNotation(idxLtp) : idxLtp || 0,
                    change:
                        typeof row.Index_change === 'string'
                            ? parseScientificNotation(row.Index_change)
                            : row.Index_change || 0,
                    changePercent:
                        typeof row.Index_changePercent === 'string'
                            ? parseScientificNotation(row.Index_changePercent)
                            : row.Index_changePercent || 0,
                    timestamp: snap.timestamp,
                    timestampIST: snap.timestampIST || '',
                }
            }
        }

        // Options Data
        if (symbol && expiry) {
            if (!snap.optionsData) snap.optionsData = {}
            const optionsData = snap.optionsData

            if (!optionsData[symbol]) optionsData[symbol] = {}
            const symbolData = optionsData[symbol]!

            if (!symbolData[expiry]) symbolData[expiry] = []
            const currentOptions = symbolData[expiry]!

            const strikeVal = Number(
                row.strikePrice ||
                    row.strike ||
                    row.Strike ||
                    row.STRIKE ||
                    row.strike_price ||
                    row['Strike Price'] ||
                    0,
            )

            const existingOpt = currentOptions.find((o: any) => o.strike === strikeVal)

            const newOptData = {
                underlyingName: symbol,
                expirationDate: expiry,
                strike: strikeVal,
                strikePrice: strikeVal, // Compatibility

                callPrice: parseScientificNotation(
                    row.callPrice ||
                        row.CE_LTP ||
                        row.call_ltp ||
                        row['CE LTP'] ||
                        row['CE Price'] ||
                        row['CE Last Price'] ||
                        0,
                ),
                callChangePercent: parseScientificNotation(
                    row.callChangePercent ||
                        row.call_change_p ||
                        row.CE_chg_p ||
                        row['CE Change'] ||
                        row['CE Price Chg'] ||
                        row['CE Change %'] ||
                        0,
                ),
                callOpenInterest: parseScientificNotation(
                    row.callOpenInterest ||
                        row.CE_OI ||
                        row.call_oi ||
                        row['CE OI'] ||
                        row['CE Open Interest'] ||
                        row['CE Open Interest (OI)'] ||
                        0,
                ),
                callVolume: parseScientificNotation(
                    row.callVolume ||
                        row.CE_Vol ||
                        row.call_volume ||
                        row['CE Vol'] ||
                        row['CE Volume'] ||
                        row['CE Total Traded Volume'] ||
                        0,
                ),
                callImpliedVolatility: parseScientificNotation(
                    row.callImpliedVolatility || row.CE_IV || row['CE IV'] || row['CE Implied Volatility'] || 0,
                ),
                callChange: parseScientificNotation(
                    row.callChange || row.CE_change || row['CE Chg'] || row['CE Change Value'] || 0,
                ),
                callOpenInterestChange: parseScientificNotation(
                    row.callOpenInterestChange || row.CE_oi_chg || row['CE OI Chg'] || row['CE Change in OI'] || 0,
                ),
                callDelta: typeof row.callDelta === 'string' ? parseFloat(row.callDelta) : row.callDelta || 0,
                callGamma: typeof row.callGamma === 'string' ? parseFloat(row.callGamma) : row.callGamma || 0,
                callTheta: typeof row.callTheta === 'string' ? parseFloat(row.callTheta) : row.callTheta || 0,
                callVega: typeof row.callVega === 'string' ? parseFloat(row.callVega) : row.callVega || 0,

                putPrice: parseScientificNotation(
                    row.putPrice ||
                        row.PE_LTP ||
                        row.put_ltp ||
                        row['PE LTP'] ||
                        row['PE Price'] ||
                        row['PE Last Price'] ||
                        0,
                ),
                putChangePercent: parseScientificNotation(
                    row.putChangePercent ||
                        row.put_change_p ||
                        row.PE_chg_p ||
                        row['PE Change'] ||
                        row['PE Price Chg'] ||
                        row['PE Change %'] ||
                        0,
                ),
                putOpenInterest: parseScientificNotation(
                    row.putOpenInterest ||
                        row.PE_OI ||
                        row.put_oi ||
                        row['PE OI'] ||
                        row['PE Open Interest'] ||
                        row['PE Open Interest (OI)'] ||
                        0,
                ),
                putVolume: parseScientificNotation(
                    row.putVolume ||
                        row.PE_Vol ||
                        row.put_volume ||
                        row['PE Vol'] ||
                        row['PE Volume'] ||
                        row['PE Total Traded Volume'] ||
                        0,
                ),
                putImpliedVolatility: parseScientificNotation(
                    row.putImpliedVolatility || row.PE_IV || row['PE IV'] || row['PE Implied Volatility'] || 0,
                ),
                putChange: parseScientificNotation(
                    row.putChange || row.PE_change || row['PE Chg'] || row['PE Change Value'] || 0,
                ),
                putOpenInterestChange: parseScientificNotation(
                    row.putOpenInterestChange || row.PE_oi_chg || row['PE OI Chg'] || row['PE Change in OI'] || 0,
                ),
                putDelta: typeof row.putDelta === 'string' ? parseFloat(row.putDelta) : row.putDelta || 0,
                putGamma: typeof row.putGamma === 'string' ? parseFloat(row.putGamma) : row.putGamma || 0,
                putTheta: typeof row.putTheta === 'string' ? parseFloat(row.putTheta) : row.putTheta || 0,
                putVega: typeof row.putVega === 'string' ? parseFloat(row.putVega) : row.putVega || 0,

                timestamp: snap.timestamp,
                timestampIST: snap.timestampIST || '',
            }

            if (existingOpt) {
                // Merge strategies:
                Object.keys(newOptData).forEach((key) => {
                    const k = key as keyof typeof newOptData
                    const newVal = newOptData[k]
                    const existingAny = existingOpt as any // Cast to any to avoid strict type checks here
                    const oldVal = existingAny[k]

                    if (typeof newVal === 'number') {
                        if (newVal !== 0 || oldVal === 0 || oldVal === undefined) {
                            existingAny[k] = newVal
                        }
                    } else if (newVal) {
                        existingAny[k] = newVal
                    }
                })
            } else {
                currentOptions.push(newOptData as unknown as OptionData)
            }
        }
    }

    // Sort logic
    const results = Array.from(snapshotsMap.values()).sort((a: any, b: any) => a.timestamp - b.timestamp)

    // Pre-calculate Analytics for each snapshot
    for (const snap of results) {
        // Identify primary symbol (first key in optionsData)
        if (!snap.optionsData) continue
        const symbols = Object.keys(snap.optionsData)
        if (symbols.length === 0) continue

        const symbol = symbols[0]
        if (!symbol) continue

        const symbolOptions = snap.optionsData[symbol]
        if (!symbolOptions) continue

        const expiryKeys = Object.keys(symbolOptions)
        if (expiryKeys.length === 0) continue

        const expiry = expiryKeys[0]
        if (!expiry) continue

        const options: any[] = symbolOptions[expiry] || []

        // Debug Log for Population
        // console.log(`[rowsToSnapshots] Snapshot ${snap.timestampIST} (${symbol}/${expiry}) -> Count: ${options.length}`);

        // Get Spot Price
        let spotPrice = 0
        if (symbol && snap.indexData?.[symbol]) {
            spotPrice = snap.indexData[symbol].lastPrice
        }

        if (options.length > 0) {
            // OPTIMIZATION: If the CRON worker already pre-computed analysis, skip expensive browser-side recalculation
            if (snap.analysis && snap.analysis.supportStrike !== undefined && snap.analysis.atmStrike !== undefined) {
                // Worker-provided analysis exists — use it directly
                // console.log(`[rowsToSnapshots] Using pre-computed analysis for ${snap.timestampIST}`);
            } else {
                // Fallback: Calculate on the browser (for CSV-sourced data that lacks pre-computed analysis)
                const stats = calculateSupportResistance(options, spotPrice, 'OI', 20, symbol as IndexSymbol)
                const variance = calculateVarianceMetrics(options, 'OI')

                snap.analysis = {
                    supportStrike: stats.support,
                    resistanceStrike: stats.resistance,
                    supportStrike2: stats.support2,
                    resistanceStrike2: stats.resistance2,
                    shift: stats.shift,
                    shiftOld: stats.shiftOld ?? stats.shift,
                    shiftV: stats.shiftV || 0,
                    shiftV_Vol: stats.shiftV_Vol || 0,
                    shiftV_OI: stats.shiftV_OI || 0,
                    v1Strike: stats.v1Strike || 0,
                    v2Strike: stats.v2Strike || 0,
                    v3Strike: stats.v3Strike || 0,
                    v1Vol: stats.v1Vol || 0,
                    v2Vol: stats.v2Vol || 0,
                    v3Vol: stats.v3Vol || 0,
                    pcr: stats.pcr,
                    pcrOI: stats.pcrOI,
                    pcrVolume: stats.pcrVolume,
                    maxPainStrike: stats.maxPainStrike,
                    maxCallOI: stats.maxCallOI || 0,
                    maxPutOI: stats.maxPutOI || 0,
                    maxCallVol: stats.maxCallVol || 0,
                    maxPutVol: stats.maxPutVol || 0,
                    totalCallOI: stats.totalCallOI || 0,
                    totalPutOI: stats.totalPutOI || 0,
                    variance: variance,
                    atmStrike: stats.atmStrike,
                }
            }
        }
    }

    return results
}

/**
 * Safe Float Parser
 */
export function safeFloat(v: any): number {
    if (typeof v === 'number') return v
    if (typeof v === 'string') {
        // Remove commas and parse
        const clean = v.replace(/,/g, '')
        if (clean === '' || clean === '-') return 0
        const n = parseFloat(clean)
        return Number.isNaN(n) ? 0 : n
    }
    return 0
}

/**
 * Normalize Snapshot (Moved from useMarketData for Worker usage)
 * Ensures consistency of timestamp and numeric fields.
 */
export function normalizeSnapshot(rawSnapshot: any): any {
    if (!rawSnapshot) return rawSnapshot
    
    // Zod validation boundary
    let s = rawSnapshot;
    try {
        if (rawSnapshot.data?.strategyChainData) {
           s = UpstoxOptionChainSchema.parse(rawSnapshot);
        }
    } catch(e) {
        console.error("Zod Upstox Validation Failed:", e);
    }

    let ts = 0
    // Prio 1: If valid Numeric Timestamp > Year 2000
    if (s.timestamp && !Number.isNaN(Number(s.timestamp)) && Number(s.timestamp) > 946684800000) {
        ts = Number(s.timestamp)
    }
    // Check for Upstox Specific structure (strategyChainData.lastUpdated)
    else if (s.strategyChainData && s.strategyChainData.lastUpdated) {
        ts = Number(s.strategyChainData.lastUpdated)
    }
    // Prio 2: Parse IST String
    else if (s.timestampIST) {
        try {
            // If already ISO format with T
            if (s.timestampIST.includes('T')) {
                ts = new Date(s.timestampIST).getTime()
            } else {
                // Parse "26-12-2025 09:00:06" -> Force IST
                const parts = s.timestampIST.split(' ')
                if (parts.length >= 2) {
                    const dateParts = parts[0].split(/[-/]/)
                    // Expect DD-MM-YYYY
                    const d = dateParts[0].padStart(2, '0')
                    const m = dateParts[1].padStart(2, '0')
                    const y = dateParts[2]
                    const t = parts[1] // HH:mm:ss

                    const isoIST = `${y}-${m}-${d}T${t}+05:30`
                    ts = new Date(isoIST).getTime()
                }
            }
        } catch (_e) {
            ts = 0
        }
    }

    if (ts === 0 || Number.isNaN(ts)) ts = Date.now()

    s.timestamp = ts

    // Upstox Specific Normalization (Flattening)
    if (s.strategyChainData && s.strategyChainData.strikeMap) {
        // 1. Map Options
        const options: any[] = []
        const strikeMap = s.strategyChainData.strikeMap
        const strikeKeys = Object.keys(strikeMap)

        strikeKeys.forEach((key) => {
            const item = strikeMap[key]
            const callOption = item.callOptionData || {}
            const putOption = item.putOptionData || {}
            const strikePrice = Number(key)

            options.push({
                strike: strikePrice,
                strikePrice: strikePrice,
                // Call
                callPrice: callOption.marketData?.ltp || 0,
                callChangePercent: callOption.marketData?.cp
                    ? (((callOption.marketData?.ltp || 0) - callOption.marketData.cp) / callOption.marketData.cp) * 100
                    : 0,
                callOpenInterest: callOption.marketData?.oi || 0,
                callVolume: callOption.marketData?.volume || 0,
                callImpliedVolatility: callOption.analytics?.iv || 0,
                callChange: callOption.marketData?.cp
                    ? (callOption.marketData?.ltp || 0) - callOption.marketData.cp
                    : 0,
                callOpenInterestChange: (callOption.marketData?.oi || 0) - (callOption.marketData?.prevOi || 0),
                callDelta: callOption.analytics?.delta || 0,
                callGamma: callOption.analytics?.gamma || 0,
                callTheta: callOption.analytics?.theta || 0,
                callVega: callOption.analytics?.vega || 0,
                callInstrumentKey: callOption.instrumentKey || '',

                // Put
                putPrice: putOption.marketData?.ltp || 0,
                putChangePercent: putOption.marketData?.cp
                    ? (((putOption.marketData?.ltp || 0) - putOption.marketData.cp) / putOption.marketData.cp) * 100
                    : 0,
                putOpenInterest: putOption.marketData?.oi || 0,
                putVolume: putOption.marketData?.volume || 0,
                putImpliedVolatility: putOption.analytics?.iv || 0,
                putChange: 0, // Not explicitly provided
                putOpenInterestChange: (putOption.marketData?.oi || 0) - (putOption.marketData?.prevOi || 0),
                putDelta: putOption.analytics?.delta || 0,
                putGamma: putOption.analytics?.gamma || 0,
                putTheta: putOption.analytics?.theta || 0,
                putVega: putOption.analytics?.vega || 0,
                putInstrumentKey: putOption.instrumentKey || '',
            })
        })

        s.options = options

        // 2. Map Index/Spot Data
        if (s.optionChainExtendedData && s.optionChainExtendedData.assetLtpc) {
            s.spotPrice = s.optionChainExtendedData.assetLtpc.ltp || 0
            s.pcr = s.optionChainExtendedData.totalPutOi / s.optionChainExtendedData.totalCallOi || 0
        }

        // 3. Map VIX
        if (s.optionChainExtendedData && s.optionChainExtendedData.indiaVixLtpc) {
            s.vix = s.optionChainExtendedData.indiaVixLtpc.ltp || 0
        }
    }

    return s
}

import type { OptionChainRow } from '../services/store'

/**
 * Adapter: Convert lightweight OptionChainRow (Store) to full OptionData (Analysis)
 * Note: Calculated fields (Change, Greeks) will be 0 as they are not in the lightweight store.
 */
export function mapChainRowToOptionData(
    row: OptionChainRow,
    symbol: string,
    expiry: string,
    timestamp: number,
): OptionData {
    // Default call/put object if missing
    const ce = row.ce || { oi: 0, ltp: 0, vol: 0 }
    const pe = row.pe || { oi: 0, ltp: 0, vol: 0 }

    return {
        underlyingName: symbol,
        expirationDate: expiry,
        strike: row.strike,
        timestamp,
        timestampIST: toIST(timestamp),

        callPrice: ce.ltp,
        callOpenInterest: ce.oi,
        callVolume: ce.vol,
        callChangePercent: ce.changePercent,
        callImpliedVolatility: ce.iv || 0,
        callDelta: ce.delta || 0,
        callTheta: ce.theta || 0,
        callGamma: ce.gamma || 0,
        callVega: ce.vega || 0,
        callInstrumentKey: ce.instrumentKey || (ce as any).ceInstrumentKey || '',

        putPrice: pe.ltp || 0,
        putOpenInterest: pe.oi || 0,
        putVolume: pe.vol || 0,
        putChangePercent: pe.changePercent || 0,
        putImpliedVolatility: pe.iv || 0,
        putDelta: pe.delta || 0,
        putTheta: pe.theta || 0,
        putGamma: pe.gamma || 0,
        putVega: pe.vega || 0,
        putInstrumentKey: pe.instrumentKey || (pe as any).peInstrumentKey || '',

        // Fill required fields
        callChange: ce.change || 0,
        putChange: pe.change || 0,
        callOpenInterestChange: ce.oiChange,
        putOpenInterestChange: pe.oiChange,
    } as OptionData
}

/**
 * Safely retrieve options for an expiry, handling strict and fuzzy date matching.
 * Used to bridge DD-MM-YYYY vs MM-DD-YYYY ambiguities.
 */
export function getOptionsForExpiry(
    optionsData: Record<string, Record<string, OptionData[]>>,
    symbol: string,
    expiry: string,
): OptionData[] {
    if (!optionsData || !symbol) return []

    // 1. Find Symbol Key — use canonical names to handle 'Nifty 50' vs 'NIFTY' etc.
    const canonicalSymbol = getCanonicalSymbol(symbol)
    const symKey = Object.keys(optionsData).find((k) => {
        const canonicalKey = getCanonicalSymbol(k)
        return canonicalKey === canonicalSymbol || k.toUpperCase() === symbol.toUpperCase()
    })
    if (!symKey) return [] // Symbol not found

    const expiries = optionsData[symKey]
    if (!expiries) return []

    // 2. Direct Match (Trimmed)
    const cleanExpiry = expiry.trim()
    if (expiries[cleanExpiry]) return expiries[cleanExpiry]

    // Debugging: Why did direct match fail?
    // console.log(`[getOptionsForExpiry] Direct Match Failed for '${expiry}' (Len: ${expiry.length}). Available:`, Object.keys(expiries).map(k => `'${k}' (${k.length})`));

    // 3. Robust Fuzzy Match
    const targetDate = parseDateStr(cleanExpiry)
    if (targetDate) {
        const targetTime = targetDate.getTime()
        const expiryKeys = Object.keys(expiries)

        for (const key of expiryKeys) {
            // Try simple string normalization first (e.g. 3-2-2026 vs 03-02-2026)
            // Also trim keys from data
            if (key.trim().replace(/-/g, '') === cleanExpiry.replace(/-/g, '')) return expiries[key] || []

            // Try Date Object Comparison
            const keyDate = parseDateStr(key.trim())
            if (keyDate && keyDate.getTime() === targetTime) {
                return expiries[key] || []
            }
        }
    }

    // 4. Absolute Fallback: Just return the first non-empty array available for this symbol.
    // (Since user explicitly only saves 1 expiry per snapshot, this is 100% safe and bypasses all format errors).
    for (const key of Object.keys(expiries)) {
        if (Array.isArray(expiries[key]) && expiries[key].length > 0) {
            return expiries[key]
        }
    }

    return []
}
